import { IGNORED_ISSUES_MAX, readIgnoreKey } from "../document/issue-ignores.ts";
import { sameJson } from "../document/same-json.ts";
import type { IssueIgnores, WorldDocument } from "../document/types.ts";
import { distinctOf, isKey, type Fields } from "./op-read.ts";
import type { IgnoreChanges, IntentOf } from "./op-types.ts";
import { mapOf, type Resolution } from "./resolve-types.ts";

/**
 * A map's ignored issues under live editing (ADR-070's amendment of 2026-10-05, decision 7 and "Who ignored
 * it"; ADR-086): how the intent `setIssueIgnores` reads from a frame and how it resolves against the record a
 * replica holds, key by key. The intent names keys and nothing else. Who ignored an issue and when is never
 * read from an operation's payload and never written to the document: a frame that carries either is refused
 * whole, since the intent has no field for it and a key's value is not the sender's to give.
 */

/**
 * Whether a value is a key an ignore may be stored under: a record key an operation may name that reads as a
 * kind and a target.
 *
 * @param value The value.
 * @returns True for such a key.
 */
function isIgnoreKey(value: unknown): value is string {
  return isKey(value) && readIgnoreKey(value) !== null;
}

/**
 * Reads the ignores an intent adds and removes.
 *
 * @param fields The intent's fields, of which `add` and `remove` are read.
 * @returns The changes, empty when the intent names neither, or undefined when either is not a list of at
 *   least one distinct key, each an ignore's key and nothing more, or a key is in both.
 */
export function ignoreChangesOf(fields: Fields): IgnoreChanges | undefined {
  const add = fields["add"] === undefined ? null : distinctOf(fields["add"], isIgnoreKey);
  const remove = fields["remove"] === undefined ? null : distinctOf(fields["remove"], isIgnoreKey);
  if (add === undefined || remove === undefined || (add !== null && add.length === 0) || (remove !== null && remove.length === 0)) {
    return undefined;
  }
  if (add !== null && remove !== null && remove.some((key) => add.includes(key))) {
    return undefined;
  }
  return { ...(add === null ? {} : { add }), ...(remove === null ? {} : { remove }) };
}

/**
 * Resolves a change of a map's ignored issues, key by key.
 *
 * @param world The document.
 * @param intent The intent.
 * @returns The command carrying the whole record before and after, its keys sorted; nothing when every key
 *   is already held or already gone; `gone` without the map; or `limit` when the record would hold more than
 *   `IGNORED_ISSUES_MAX` and more than it does now.
 */
export function resolveSetIssueIgnores(world: WorldDocument, intent: IntentOf<"setIssueIgnores">): Resolution {
  const map = mapOf(world, intent.map);
  if (map === undefined) {
    return "gone";
  }
  const before: IssueIgnores = map.ignoredIssues ?? {};
  const next = new Set(Object.keys(before));
  for (const key of intent.add ?? []) {
    next.add(key);
  }
  for (const key of intent.remove ?? []) {
    next.delete(key);
  }
  const after: IssueIgnores = Object.fromEntries([...next].sort().map((key) => [key, true as const]));
  if (sameJson(before, after)) {
    return [];
  }
  if (next.size > IGNORED_ISSUES_MAX && next.size > Object.keys(before).length) {
    return "limit";
  }
  return [{ kind: "setIssueIgnores", mapId: map.id, before, after }];
}
